import { createAtom } from '@xstate/store';

import { AudioFeatureType } from '../../api/cc-smarts/models/AudioFeature';
import type { ActiveContent } from '../../types/activeContent';
import { AUDIO_CONTAINER_MODES, type AudioContainerMode } from '../../types/audioContainerMode';
import type {
	ConfluenceLength,
	ConfluenceTone,
} from '../ui/audio-player/__generated__/AudioContainerQuery.graphql';
import type { AudioFeatureMenuItem } from '../ui/audio-player/menu/AudioFeatureMenuGroup';

const DEFAULT_AUDIO_FEATURE = AudioFeatureType.BRIEFING;

export type AudioPlayerState = {
	activeContent: ActiveContent;
	activeFeature: AudioFeatureType;
	activeMenu?: AudioFeatureMenuItem;
	activeAudioContainerMode: AudioContainerMode;
	pendingContent: ActiveContent | null;
	isOpen: boolean;
	isPlaying: boolean;
	isPaused: boolean;
	product: string;
	cloudId: string;
	activationId: string;
	customization: {
		tone: ConfluenceTone;
		length: ConfluenceLength;
		playbackSpeed: number;
	};
	playlistDefaultExpanded?: boolean;
	playImmediately?: boolean;
	playlistHasUnavailableContent: boolean;
};

const DEFAULT_CUSTOMIZATION: AudioPlayerState['customization'] = {
	tone: 'PROFESSIONAL',
	length: 'SHORT',
	playbackSpeed: 1.0,
};

const initialState: AudioPlayerState = {
	activeContent: {
		contentId: '',
		spaceKey: '',
		contentType: '',
		subType: '',
		title: '',
		author: '',
		avatarUrl: '',
		supportingContent: [],
		mainContentLink: '',
		emojiId: undefined,
	},
	activeFeature: DEFAULT_AUDIO_FEATURE,
	activeAudioContainerMode: AUDIO_CONTAINER_MODES.EXPANDED,
	pendingContent: null,
	isOpen: false,
	isPlaying: false,
	isPaused: false,
	product: '',
	cloudId: '',
	activationId: '',
	customization: DEFAULT_CUSTOMIZATION,
	playlistDefaultExpanded: false,
	playImmediately: false,
	playlistHasUnavailableContent: false,
};

export const audioPlayerState = createAtom<AudioPlayerState>(initialState);

export const setActiveContent = (activeContent: ActiveContent) => {
	audioPlayerState.set((prevState) => ({
		...prevState,
		activeContent,
		// Reset playback states when switching to a new track
		isPlaying: false,
		isPaused: false,
	}));
};

export const setActiveFeature = (activeFeature: AudioFeatureType) => {
	audioPlayerState.set((prevState) => ({ ...prevState, activeFeature }));
};

export const setActiveMenu = (activeMenu: AudioFeatureMenuItem | undefined) => {
	audioPlayerState.set((prevState) => ({ ...prevState, activeMenu }));
};

export const setActiveAudioContainerMode = (activeAudioContainerMode: AudioContainerMode) => {
	audioPlayerState.set((prevState) => ({ ...prevState, activeAudioContainerMode }));
};

export const openAudioPlayer = (
	contentToOpen: ActiveContent,
	options?: { isUninitialized?: boolean; playImmediately?: boolean; expandPlaylist?: boolean },
) => {
	audioPlayerState.set((prevState) => {
		// Check if the content is already active
		const isAlreadyActive = prevState.activeContent.contentId === contentToOpen.contentId;

		// Check if the content is already pending
		const isAlreadyPending = prevState.pendingContent?.contentId === contentToOpen.contentId;

		const updates: Partial<AudioPlayerState> = {};

		updates.playImmediately = options?.playImmediately || false;
		updates.playlistDefaultExpanded = options?.expandPlaylist || false;

		// Handle expandPlaylist logic
		if (options?.expandPlaylist) {
			// If player is not open, set active content and open the playlist
			if (!prevState.isOpen) {
				return { ...prevState, activeContent: contentToOpen, isOpen: true, ...updates };
			}
			// If player is already open, just expand the playlist (don't change active content)
			return {
				...prevState,
				...updates,
			};
		}

		// If content is already active or pending, only apply updates (like expandPlaylist)
		if (isAlreadyActive || isAlreadyPending) {
			return { ...prevState, ...updates };
		}

		// If the player is uninitialized or playImmediately is true, set the new content as active
		if (options?.isUninitialized || options?.playImmediately) {
			return { ...prevState, activeContent: contentToOpen, isOpen: true, ...updates };
		}

		// If player is already open, set new content as pending
		if (prevState.isOpen) {
			return { ...prevState, pendingContent: contentToOpen, ...updates };
		}
		// Otherwise, set as active content and open player
		return { ...prevState, activeContent: contentToOpen, isOpen: true, ...updates };
	});

	// Expand audio player every time it's opened
	setActiveAudioContainerMode(AUDIO_CONTAINER_MODES.EXPANDED);
};

export const activatePendingContent = () => {
	audioPlayerState.set((prevState) => {
		if (prevState.pendingContent) {
			return {
				...prevState,
				activeContent: prevState.pendingContent,
				pendingContent: null,
				isPlaying: false,
				isPaused: false,
			};
		}
		return prevState;
	});
};

export const clearPendingContent = () => {
	audioPlayerState.set((prevState) => ({
		...prevState,
		pendingContent: null,
	}));
};

export const closeAudioPlayer = () => {
	audioPlayerState.set(initialState);
};

export const setIsPlaying = (isPlaying: boolean) => {
	audioPlayerState.set((prevState) => ({ ...prevState, isPlaying }));
};

export const setIsPaused = (isPaused: boolean) => {
	audioPlayerState.set((prevState) => ({ ...prevState, isPaused }));
};

// Convenience action to update multiple playback properties at once
export const updatePlayerState = (updates: Partial<AudioPlayerState>) => {
	audioPlayerState.set((prevState) => ({ ...prevState, ...updates }));
};

export const setCustomization = (newCustomization: AudioPlayerState['customization']) => {
	audioPlayerState.set((prevState) => {
		const { customization } = prevState;
		const updatedCustomization = { ...customization, ...newCustomization };

		return { ...prevState, customization: updatedCustomization };
	});
};

export const getCustomization = () => {
	return audioPlayerState.get().customization;
};

export const getActiveContent = () => {
	return audioPlayerState.get().activeContent;
};

export const setSupportingContent = (supportingContent: ActiveContent['supportingContent']) => {
	audioPlayerState.set((prevState) => ({
		...prevState,
		activeContent: { ...prevState.activeContent, supportingContent },
	}));
};

export const getSupportingContent = () => {
	return audioPlayerState.get().activeContent.supportingContent;
};

export const setPlaybackSpeed = (playbackSpeed: number) => {
	audioPlayerState.set((prevState) => ({
		...prevState,
		customization: { ...prevState.customization, playbackSpeed },
	}));
};

export const resetPlaylistDefaultExpanded = () => {
	audioPlayerState.set((prevState) => ({
		...prevState,
		playlistDefaultExpanded: false,
	}));
};

export const resetPlayImmediately = () => {
	audioPlayerState.set((prevState) => ({
		...prevState,
		playImmediately: false,
	}));
};

export const setPlaylistHasUnavailableContent = (playlistHasUnavailableContent: boolean) => {
	audioPlayerState.set((prevState) => ({
		...prevState,
		playlistHasUnavailableContent,
	}));
};
